import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useDeliveryAddress } from '../context/DeliveryAddressContext';
import { API_URL } from '../config/api';
import { getFullImageUrl } from '../utils/imageUtils';
import { isCurrentlyOpen, getTodayOpeningHours } from '../utils/openingHours';
import ItemDetailModal from '../components/ItemDetailModal';
import SEO from '../components/SEO';
import './RestaurantDetail.css';

const RestaurantDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { addToCart } = useCart();
  const { user, updateUser } = useAuth();
  const { currentAddress, addressVersion } = useDeliveryAddress();
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [showItemModal, setShowItemModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [showOpeningHours, setShowOpeningHours] = useState(false);
  const [showContactInfo, setShowContactInfo] = useState(false);
  const lastFetchedCoordinates = useRef(null);

  const fetchRestaurant = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      let url = `${API_URL}/restaurants/${id}`;
      let coordinatesKey = null;
      // Add user location if available
      if (currentAddress?.coordinates?.lat && currentAddress?.coordinates?.lng) {
        const lat = parseFloat(currentAddress.coordinates.lat);
        const lng = parseFloat(currentAddress.coordinates.lng);
        if (!isNaN(lat) && !isNaN(lng)) {
          url += `?lat=${lat}&lng=${lng}`;
          coordinatesKey = `${lat},${lng}`;
        }
      }
      const response = await axios.get(url);
      setRestaurant(response.data);
      lastFetchedCoordinates.current = coordinatesKey;
    } catch (error) {
      console.error('Error fetching restaurant:', error);
    } finally {
      setLoading(false);
    }
  }, [id, currentAddress, addressVersion]);

  useEffect(() => {
    fetchRestaurant();
  }, [fetchRestaurant]);

  // Refetch when currentAddress becomes available (e.g., loaded from localStorage)
  // This ensures distance is calculated even if address loads after initial fetch
  useEffect(() => {
    if (id && currentAddress?.coordinates?.lat && currentAddress?.coordinates?.lng) {
      const lat = parseFloat(currentAddress.coordinates.lat);
      const lng = parseFloat(currentAddress.coordinates.lng);
      if (!isNaN(lat) && !isNaN(lng)) {
        const coordinatesKey = `${lat},${lng}`;
        // Refetch if we haven't fetched with these coordinates yet, or if restaurant doesn't have distance
        if (lastFetchedCoordinates.current !== coordinatesKey || 
            !restaurant || 
            restaurant.distance === undefined || 
            restaurant.distance === null) {
          fetchRestaurant();
        }
      }
    }
  }, [id, currentAddress?.coordinates?.lat, currentAddress?.coordinates?.lng, addressVersion, fetchRestaurant]);

  useEffect(() => {
    if (user && restaurant) {
      setIsFavorite(user.favoriteRestaurants?.includes(restaurant._id) || false);
    }
  }, [user, restaurant]);


  const getMenuCategories = () => {
    if (!restaurant?.menu) return [];
    const categories = [...new Set(restaurant.menu.map(item => item.category))];
    return ['All', ...categories];
  };

  const getFilteredMenu = () => {
    if (!restaurant?.menu) return [];
    if (selectedCategory === 'All') return restaurant.menu;
    return restaurant.menu.filter(item => item.category === selectedCategory);
  };

  const isOpen = () => isCurrentlyOpen(restaurant);

  const handleToggleFavorite = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (isFavorite) {
        await axios.delete(`${API_URL}/users/favorites/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsFavorite(false);
        if (user.favoriteRestaurants) {
          user.favoriteRestaurants = user.favoriteRestaurants.filter(r => r.toString() !== id);
          updateUser(user);
        }
      } else {
        await axios.post(`${API_URL}/users/favorites/${id}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsFavorite(true);
        if (!user.favoriteRestaurants) {
          user.favoriteRestaurants = [];
        }
        user.favoriteRestaurants.push(id);
        updateUser(user);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const renderStars = (rating) => {
    if (rating == null || rating <= 0) return null;
    const stars = [];
    const fullStars = Math.min(5, Math.max(0, Math.round(rating)));
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <i key={i} className={i <= fullStars ? 'fas fa-star' : 'far fa-star'}></i>
      );
    }
    return stars;
  };

  if (loading) {
    return <div className="restaurant-detail-container"><div className="container">Loading...</div></div>;
  }

  if (!restaurant) {
    return <div className="restaurant-detail-container"><div className="container">Restaurant not found</div></div>;
  }

  const menuCategories = getMenuCategories();
  const filteredMenu = getFilteredMenu();

  return (
    <div className="restaurant-detail-container">
      <SEO
        title={restaurant.name}
        description={restaurant.description?.trim() || `${restaurant.name} – tilaa ruoka kotiin Pussikuskin kautta.`}
        image={restaurant.banners?.[0] ? getFullImageUrl(restaurant.banners[0]) : undefined}
        path={`/restaurant/${restaurant._id}`}
      />
      <div className="restaurant-detail">
        {/* Banner Background */}
        {restaurant.banners && restaurant.banners.length > 0 && (
          <div 
            className="restaurant-banner-background"
            style={{ backgroundImage: `url(${getFullImageUrl(restaurant.banners[0])})` }}
          >
            <button 
              className={`banner-favorite-btn ${isFavorite ? 'active' : ''}`}
              onClick={handleToggleFavorite}
              title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
            >
              <i className={isFavorite ? 'fas fa-heart' : 'far fa-heart'}></i>
            </button>
            <div className={`restaurant-status-overlay ${isOpen() ? 'open' : 'closed'}`}>
              {restaurant.closed
                ? `🔴 ${t('restaurant.orderingPausedDueToCongestion')}`
                : isOpen()
                  ? `🟢 ${t('restaurant.open')}`
                  : `🔴 ${t('restaurant.closed')}`}
            </div>
            {currentAddress?.coordinates?.lat &&
             currentAddress?.coordinates?.lng &&
             typeof restaurant.distance === 'number' &&
             !isNaN(restaurant.distance) &&
             restaurant.distance > 0 && (() => {
              const maxDist = restaurant.maxDeliveryDistance ?? 20;
              if (restaurant.distance > maxDist) {
                return (
                  <div className="restaurant-delivery-overlay-banner">
                    {t('restaurant.outsideDeliveryArea')}
                  </div>
                );
              }
              return null;
            })()}
          </div>
        )}

        <div className="restaurant-detail-header">
          <div className="restaurant-title-row">
            <div className="restaurant-title-left">
              <h1>{restaurant.name}</h1>
              {restaurant.description && <p className="description">{restaurant.description}</p>}
              <div className="pricing-range">{restaurant.pricingRange}</div>
            </div>
            {(restaurant.settings?.displayStarRatings !== false && restaurant.rating != null && restaurant.rating > 0) && (
              <div className="restaurant-title-right">
                <div className="rating-stars">
                  {renderStars(restaurant.rating)}
                </div>
              </div>
            )}
          </div>
          
          <div className="restaurant-meta-info">
            {(restaurant.distance != null && !isNaN(Number(restaurant.distance)) && Number(restaurant.distance) > 0) ? (
              <span className="distance">{t('restaurant.distanceAway', { distance: Number(restaurant.distance).toFixed(1) })}</span>
            ) : null}
            {(() => {
              const today = getTodayOpeningHours(restaurant);
              if (today) {
                return <span className="hours">{today.openingTime} – {today.closingTime}</span>;
              }
              if (restaurant?.openingHours) {
                return <span className="hours">{t('restaurant.closedToday') || 'Closed today'}</span>;
              }
              return null;
            })()}
          </div>

          {/* Delivery Zone Warning */}
          {currentAddress?.coordinates?.lat && 
           currentAddress?.coordinates?.lng &&
           typeof restaurant.distance === 'number' &&
           !isNaN(restaurant.distance) &&
           restaurant.distance > 0 && (
            (() => {
              const maxDist = restaurant.maxDeliveryDistance ?? 20; // Default 20km if not set
              if (restaurant.distance > maxDist) {
                return (
                  <div className="delivery-zone-warning">
                    <i className="fas fa-exclamation-triangle"></i>
                    <div className="warning-content">
                      <strong>{t('restaurant.notDeliveringToLocation')}</strong>
                      <p>{t('restaurant.deliveryZoneDescription', { distance: restaurant.distance.toFixed(1), max: maxDist })}</p>
                    </div>
                  </div>
                );
              }
              return null;
            })()
          )}

          <div className="restaurant-divider"></div>

          <div className="delivery-info-section">
            <div className="delivery-info-item">
              <i className="fas fa-clock"></i>
              <span>{restaurant.deliveryTime || '30-45 min'}</span>
            </div>
            <div className="delivery-info-item">
              <i className="fas fa-euro-sign"></i>
              <span>{t('restaurant.deliveryFee')}: €{restaurant.deliveryFee ?? '0'}</span>
            </div>
            <div className="delivery-info-item">
              <i className="fas fa-shopping-cart"></i>
              <span>{t('restaurant.minimumOrder')}: €{(restaurant.minOrderPrice ?? restaurant.minimumOrder ?? 0).toFixed(2)}</span>
            </div>
          </div>

          {restaurant.openingHours && (
            <div className="opening-hours-section">
              <button
                type="button"
                className="opening-hours-toggle"
                onClick={() => setShowOpeningHours((v) => !v)}
                aria-expanded={showOpeningHours}
              >
                {t('restaurant.viewOpeningHours')}
                <i className={`fas fa-chevron-${showOpeningHours ? 'up' : 'down'}`} aria-hidden="true"></i>
              </button>
              {showOpeningHours && (
                <>
                  <h3 className="opening-hours-title">{t('restaurant.openingHours')}</h3>
                  <ul className="opening-hours-list">
                    {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => {
                      const dayHours = restaurant.openingHours[day];
                      const isOpen = dayHours?.isOpen !== false && dayHours?.openingTime && dayHours?.closingTime;
                      return (
                        <li key={day} className="opening-hours-row">
                          <span className="opening-hours-day">{t(`restaurant.${day}`)}</span>
                          <span className="opening-hours-time">
                            {isOpen ? `${dayHours.openingTime} – ${dayHours.closingTime}` : t('restaurant.closed')}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
            </div>
          )}

          {(restaurant.phoneNumber || restaurant.contactInformation) && (
            <div className="contact-info-section">
              <button
                type="button"
                className="opening-hours-toggle contact-info-toggle"
                onClick={() => setShowContactInfo((v) => !v)}
                aria-expanded={showContactInfo}
              >
                {t('restaurant.contactInformation')}
                <i className={`fas fa-chevron-${showContactInfo ? 'up' : 'down'}`} aria-hidden="true"></i>
              </button>
              {showContactInfo && (
                <div className="contact-info-content">
                  {restaurant.phoneNumber && (
                    <p className="contact-phone">
                      <a href={`tel:${restaurant.phoneNumber.replace(/\s/g, '')}`}>{restaurant.phoneNumber}</a>
                    </p>
                  )}
                  {restaurant.contactInformation && (
                    <p className="contact-text">{restaurant.contactInformation}</p>
                  )}
                </div>
              )}
            </div>
          )}

          {restaurant.deliveryMethods && (
            <div className="delivery-methods-info">
              {restaurant.deliveryMethods.delivery && (
                <span className="delivery-method-tag">{t('restaurant.delivery')}</span>
              )}
              {restaurant.deliveryMethods.pickup && (
                <span className="delivery-method-tag">{t('restaurant.pickup')}</span>
              )}
              {restaurant.deliveryMethods.eatIn && (
                <span className="delivery-method-tag">{t('restaurant.eatIn')}</span>
              )}
            </div>
          )}
        </div>

        {/* Menu Categories */}
        {menuCategories.length > 1 && (
          <div className="menu-categories">
            {menuCategories.map(category => (
              <button
                key={category}
                className={`category-btn ${selectedCategory === category ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>
        )}

        {/* Menu Items */}
        <div className="menu-section">
          <h2>Menu</h2>
          {filteredMenu.length === 0 ? (
            <p>No items in this category</p>
          ) : (
            <div className="menu-items">
              {filteredMenu.map((item) => (
                <div 
                  key={item._id} 
                  className="menu-item-card"
                  onClick={() => {
                    setSelectedItem(item);
                    setShowItemModal(true);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="menu-item-content">
                    <div className="menu-item-left">
                      {item.image ? (
                        <div className="menu-item-image-container">
                          <img src={getFullImageUrl(item.image)} alt={item.name} className="menu-item-image" loading="lazy" decoding="async" />
                        </div>
                      ) : (
                        <div className="menu-item-image-container">
                          <div className="menu-item-image-placeholder">
                            <i className="fas fa-image"></i>
                          </div>
                        </div>
                      )}
                      <div className="menu-item-price-below-image">
                        <span className="item-price">€{item.price != null ? Number(item.price).toFixed(2) : '0.00'}</span>
                        {restaurant.settings?.trackStock && (item.stockQty == null || item.stockQty <= 0) && (
                          <span className="sold-out-badge">{t('restaurant.soldOut') || 'Sold out'}</span>
                        )}
                      </div>
                    </div>
                    <div className="menu-item-text">
                      <h3>{item.name}</h3>
                      {item.description && <p className="item-description">{item.description}</p>}
                      {item.category && <span className="item-category">{item.category}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {showItemModal && selectedItem && (
        <ItemDetailModal
          isOpen={showItemModal}
          onClose={() => {
            setShowItemModal(false);
            setSelectedItem(null);
          }}
          item={selectedItem}
          restaurantId={restaurant._id}
          restaurantName={restaurant.name}
          isProduct={false}
          orderingDisabled={restaurant.closed || (!!restaurant.settings?.trackStock && (selectedItem?.stockQty == null || selectedItem?.stockQty <= 0))}
          soldOut={!!restaurant.settings?.trackStock && (selectedItem?.stockQty == null || selectedItem?.stockQty <= 0)}
        />
      )}
    </div>
  );
};

export default RestaurantDetail;
